import { useBusinessService } from '@ingenico/react-section-utils';
import { useSafeLeakSubscriber } from '@ingenico/react-utils/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { catchError, delay, distinctUntilChanged, EMPTY, mergeMap, of, startWith, Subject, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScreenShareService } from '../../domain/business/services/screen-share.service';
import type { ScreenShareInitStatus } from '../../domain/models/screenShare.model';
import { ScreenShareSessionStatus } from '../../domain/models/screenShare.model';
import type {
  IUseLogicalDeviceScreenSharePageVMProps,
  UseLogicalDeviceScreenSharePageVmPageReturn,
} from './logicalDeviceScreenSharePage.model';

export function useLogicalDeviceScreenSharePageVm({
  logicalDeviceId,
  deviceModel = 'deviceModel',
  deviceName = 'deviceName',
  serialNumber = 'serialNumber',
}: IUseLogicalDeviceScreenSharePageVMProps): UseLogicalDeviceScreenSharePageVmPageReturn {
  const screenShareService = useBusinessService(ScreenShareService);
  const screenShareContainer = useRef<HTMLElement | null>(null);
  const startScreenShareSubject = useRef(new Subject<void>()).current;
  const startScreenShareSubscriber = useSafeLeakSubscriber([screenShareContainer.current, screenShareService]);
  const getInitStatusSubscriber = useSafeLeakSubscriber<ScreenShareInitStatus>([
    screenShareContainer.current,
    screenShareService,
  ]);
  const getScreenShareSessionStatusSubscriber = useSafeLeakSubscriber<ScreenShareSessionStatus>([
    screenShareContainer.current,
    screenShareService,
  ]);
  const [initStatus, setInitStatus] = useState<ScreenShareInitStatus | null>(null);
  const [sessionStatus, setSessionStatus] = useState<ScreenShareSessionStatus>(ScreenShareSessionStatus.DISCONNECTED);
  const [isModalOpened, setModalOpened] = useState<boolean>(true);
  const handleStopSession = useCallback(() => screenShareService.closeScreenShare(), [screenShareService]);
  const handleBackButton = useCallback(() => screenShareService.triggerBackButton(), [screenShareService]);
  const handleAppsButton = useCallback(() => screenShareService.triggerAppsButton(), [screenShareService]);
  const handleHomeButton = useCallback(() => screenShareService.triggerHomeButton(), [screenShareService]);
  const handleRetry = useCallback(() => startScreenShareSubject.next(void 0), [startScreenShareSubject]);
  const handleCloseModal = useCallback(() => {
    if (![ScreenShareSessionStatus.CONNECTED, ScreenShareSessionStatus.RefusedByCustomer].includes(sessionStatus)) {
      screenShareService.closeScreenShare();
    }
    setModalOpened(false);
  }, [screenShareService, sessionStatus]);

  useEffect(() => {
    const screenShareContainerRef = screenShareContainer.current;
    if (screenShareContainerRef) {
      getInitStatusSubscriber(screenShareService.getInitStatus().pipe(tap((status) => setInitStatus(status))));
      getScreenShareSessionStatusSubscriber(
        screenShareService.getScreenShareSessionStatus().pipe(
          distinctUntilChanged(),
          tap((status) => {
            setSessionStatus(status);
          }),
          filter((status) => status === ScreenShareSessionStatus.CONNECTED),
          delay(2000),
          tap(() => setModalOpened(false)),
        ),
      );
      startScreenShareSubscriber(
        startScreenShareSubject.asObservable().pipe(
          startWith(void 0),
          switchMap(() =>
            of(void 0).pipe(
              tap(() => setModalOpened(true)),
              mergeMap(() => screenShareService.launchScreenShare(screenShareContainerRef, logicalDeviceId)),
              catchError(() => EMPTY),
            ),
          ),
        ),
      );
    }
  }, [
    getInitStatusSubscriber,
    getScreenShareSessionStatusSubscriber,
    logicalDeviceId,
    screenShareContainer,
    screenShareService,
    startScreenShareSubject,
    startScreenShareSubscriber,
  ]);

  return {
    initStatus,
    sessionStatus,
    handleStopSession,
    handleRetry,
    handleHomeButton,
    handleAppsButton,
    handleBackButton,
    screenShareContainer,
    isModalOpened,
    handleCloseModal,
    data: { deviceModel, deviceName, serialNumber },
  };
}
